import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useNavigate, useParams, Link } from 'react-router-dom';
import Navbar from '../Navbar';
import Header from '../Header';
import baseUrl from '../BaseUrl';
import EmptyState from '../active-users/states/EmptyState';
import ErrorState from '../active-users/states/ErrorState';
import TableSkeleton from '../active-users/states/TableSkeleton';
import '../active-users/active-users.css';
import './ceo-requests.css';
import { formatUTCDate } from '../../utils/utils';

/**
 * One CEO request, in full (feature 066-ceo-requests). JWT-protected.
 *
 * Same shell, head bar and state components as every other portal detail page. A ROUTE, not a
 * modal — so browser Back returns to the list with its filters still applied.
 *
 * THREE THINGS THIS PAGE EXISTS TO GET RIGHT:
 *
 * 1. The message is rendered inside a plain {description} expression. React escapes that by
 *    default and the default IS the security control: the text is untrusted member input, so
 *    dangerouslySetInnerHTML must never appear here (FR-018). Paragraph structure is preserved
 *    by `white-space: pre-wrap` in the stylesheet, not by converting newlines to markup.
 *
 * 2. "Not found" is a DIFFERENT state from "failed to load". A 404 means this request does not
 *    exist; an error means we could not ask. Collapsing them would have an admin hunting for a
 *    deleted request that is actually sitting there behind an outage (FR-052).
 *
 * 3. Both clocks are shown. `createdAt` is the member's LOCAL wall clock, so the local time is
 *    what they saw and the UTC time is the only unambiguous instant. Showing one without the
 *    other makes a request from another timezone impossible to place.
 */
export default function CeoRequestDetail() {
  const navigate = useNavigate();
  const { requestId } = useParams();
  const [token, setToken] = useState('');
  const [expire, setExpire] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notFound, setNotFound] = useState(false);
  const [request, setRequest] = useState(null);

  const axiosJWT = axios.create();
  axiosJWT.interceptors.request.use(
    async (config) => {
      if (expire * 1000 < new Date().getTime()) {
        const res = await axios.get(`${baseUrl}/admin/api/token`);
        config.headers.Authorization = `Bearer ${res.data.accessToken}`;
        setToken(res.data.accessToken);
        setExpire(jwtDecode(res.data.accessToken).exp);
      }
      return config;
    },
    (err) => Promise.reject(err)
  );

  useEffect(() => { refreshToken(); }, []);

  const refreshToken = async () => {
    try {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    } catch (err) {
      if (err.response) { navigate('/'); }
    }
  };

  useEffect(() => {
    if (token) { load(); }
    /* eslint-disable-next-line */
  }, [token]);

  const load = async () => {
    setLoading(true);
    setError('');
    setNotFound(false);
    try {
      const res = await axiosJWT.get(`${baseUrl}/admin/api/ceo-requests/${requestId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRequest((res.data && res.data.data) || null);
    } catch (e) {
      // 404 and 500 mean different things to an admin — keep them apart.
      if (e.response && e.response.status === 404) {
        setNotFound(true);
      } else {
        setError('Failed to load this request.');
      }
      setRequest(null);
    } finally {
      setLoading(false);
    }
  };

  const field = (value) => value || <span className="cr-unavailable">Not provided</span>;

  return (
    <div className="panelbox">
      <Navbar />
      <div className="right-panel">
        <Header name="Pauser" />
        <div className="content-page">
          <div className="au-head">
            <span className="au-breadcrumb">Feedback › CEO Requests › <b>Request</b></span>
            <span className="au-route">/ceo-requests/{requestId}</span>
            <span className="au-spacer" style={{ flex: 1 }} />
            <span className="au-back"><Link to="/user-feedback?tab=ceo">← Back to requests</Link></span>
          </div>

          <h1 className="au-title cr-title">CEO request</h1>

          {loading ? (
            <TableSkeleton rows={5} />
          ) : notFound ? (
            <EmptyState
              title="Request not found"
              message="No CEO request exists with this ID. It may have been removed from Firebase."
              icon="🔎"
            />
          ) : error ? (
            <ErrorState message={error} onRetry={load} />
          ) : request ? (
            <>
              <section className="cr-detail__member" aria-label="Sender">
                <dl>
                  <div>
                    <dt>From</dt>
                    <dd>{field(request.user_name)}</dd>
                  </div>
                  <div>
                    <dt>Email</dt>
                    <dd>{field(request.user_email)}</dd>
                  </div>
                  <div>
                    <dt>Member&rsquo;s local time</dt>
                    <dd>{field(formatUTCDate(request.created_display))}</dd>
                  </div>
                  <div>
                    <dt>UTC time</dt>
                    <dd>{field(formatUTCDate(request.utc_display))}</dd>
                  </div>
                  <div>
                    <dt>Push token on file</dt>
                    <dd>{request.has_fcm_token ? 'Yes' : 'No'}</dd>
                  </div>
                </dl>
              </section>

              <section className="cr-request" aria-label="Message">
                <p className="cr-request__eyebrow">What they wrote</p>
                {request.description ? (
                  <div className="cr-message">{request.description}</div>
                ) : (
                  <div className="cr-message cr-message--empty">
                    This request was sent without a message.
                  </div>
                )}
              </section>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
